<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import IconButton from "@/components/ui/IconButton/IconButton.vue";
import StatusDot from "@/components/ui/StatusDot.vue";
import type { Iteration } from "@/model/session";
import type { StatusDistributionSource } from "@/model/status";
import { useSearchMenuStore } from "@/store/search/searchMenu";

import DateBanner from "./internal/DateBanner.vue";
import type {
  StatusDistributionFilters,
  StatusDistributionRow,
} from "./internal/useStatusDistribution";
import { useStatusDistribution } from "./internal/useStatusDistribution";

interface Props {
  filters: StatusDistributionFilters;
  source: StatusDistributionSource;
  iteration?: Iteration;
  title: string;
  showActions?: boolean;
}

const props = withDefaults(defineProps<Props>(), { iteration: undefined, showActions: true });

const { t } = useI18n();
const searchMenuStore = useSearchMenuStore();

function isStatusActive(row: StatusDistributionRow): boolean {
  const current = searchMenuStore.selectedStatusDistribution;

  if (current.type !== "statusClass") {
    return false;
  }

  // Check if status class and sticky IDs match
  return (
    current.statusClass === row.statusClass &&
    JSON.stringify(current.stickyIds) === JSON.stringify(row.stickyIds)
  );
}

function handleViewStickies(row: StatusDistributionRow) {
  if (isStatusActive(row)) {
    // If the same status is clicked, toggle it off
    searchMenuStore.selectedStatusDistribution = { type: "none" };
  } else {
    // Set the new status filter with the sticky IDs from backend
    // Include parentStickyId if this is filtering from a sticky context
    searchMenuStore.selectedStatusDistribution = {
      type: "statusClass",
      statusClass: row.statusClass,
      stickyIds: row.stickyIds,
      parentStickyId: props.filters.stickyId,
    };
  }
}

const {
  data: fetchedData,
  loading,
  error,
  fetch,
} = useStatusDistribution({
  filters: computed(() => props.filters),
  source: props.source,
  iteration: props.iteration,
});

// Always use fetched data to ensure we have sticky IDs from backend
const data = computed(() => fetchedData.value);

onMounted(() => {
  // Always fetch to get sticky IDs from backend
  void fetch();
});

const emptyMessage = computed(() => {
  const sourceKey = props.source.charAt(0).toUpperCase() + props.source.slice(1);
  return t(`statusDistribution.empty${sourceKey}`);
});

const totalStoryPoints = computed(() =>
  data.value.rows.reduce((sum, row) => sum + row.totalStoryPoints, 0),
);

const totalWorkItems = computed(() =>
  data.value.rows.reduce((sum, row) => sum + row.itemsCount, 0),
);
</script>

<template>
  <div class="status-distribution-detailed">
    <!-- Popup Header with Title -->
    <div class="popup-header">
      <div class="header-title">
        <span class="title">{{ title }}</span>
      </div>
    </div>

    <!-- Date Banner -->
    <div v-if="iteration" class="date-banner-container">
      <DateBanner :iteration />
    </div>

    <!-- Status Table -->
    <div class="table-container">
      <div v-if="loading" class="loading-state">{{ t("loadCapacityChart.loading") }}</div>
      <div v-else-if="error" class="error-state">{{ t("general.failedToLoad") }}</div>
      <div v-else-if="data.rows.length === 0" class="empty-state">{{ emptyMessage }}</div>
      <table v-else :class="{ 'no-actions': !showActions }">
        <thead>
          <tr>
            <th class="status-header">{{ t("general.status") }}</th>
            <th class="numeric-header">{{ t("statusDistribution.storyPoints") }}</th>
            <th class="numeric-header">{{ t("statusDistribution.workItems") }}</th>
            <th class="numeric-header">{{ t("label.percentSign") }}</th>
            <th v-if="showActions" class="action-header">{{ t("general.action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in data.rows" :key="index">
            <th scope="row" class="status-cell">
              <span class="status-dot-wrapper">
                <StatusDot radar-type :status-class="row.statusClass" />
              </span>
              <span>{{ row.statusName }}</span>
            </th>
            <td class="numeric-cell">
              {{
                t("general.partOfTotal", { part: row.totalStoryPoints, total: totalStoryPoints })
              }}
            </td>
            <td class="numeric-cell">
              {{ t("general.partOfTotal", { part: row.itemsCount, total: totalWorkItems }) }}
            </td>
            <td class="numeric-cell">
              {{
                t("general.percent", { value: Math.round((row.itemsCount / totalWorkItems) * 100) })
              }}
            </td>
            <td v-if="showActions" class="action-cell">
              <IconButton
                icon="general/view-status"
                :text="t('statusDistribution.highlightStickies')"
                :activated="isStatusActive(row)"
                @click="handleViewStickies(row)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.status-distribution-detailed {
  pointer-events: all;
  display: flex;
  flex-direction: column;
  width: 540px;
  max-height: 600px;
  background: colors.$background-ui;
  border-radius: 8px;
  overflow: hidden;
}

.popup-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid colors.$border-subtle;
  background: colors.$background-ui;

  .header-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  .title {
    @include typography.small("bold");

    color: colors.$text;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.date-banner-container {
  padding: 16px;
  padding-bottom: 0;
  background: colors.$background-ui;

  :deep(.date-banner) {
    background: colors.$background-grey-light;
    color: colors.$text;
  }
}

.table-container {
  overflow-y: auto;
  flex: 1;
  padding-bottom: 8px;
}

.empty-state,
.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: colors.$text-placeholder;

  @include typography.medium("regular");
}

.error-state {
  color: colors.$destructive;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
}

thead {
  tr {
    height: 36px;
    border-bottom: 1px solid colors.$border-subtle;
  }

  th {
    @include typography.small("semi-bold");

    color: colors.$text-subtle;
    padding: 0 16px;
    font-weight: inherit;
    white-space: nowrap;
    vertical-align: middle;

    &:first-child {
      padding-left: 16px;
    }

    &:last-child {
      padding-right: 16px;
    }
  }
}

tbody {
  tr {
    height: 36px;
  }

  td,
  th[scope="row"] {
    @include typography.medium("semi-bold");

    color: colors.$text;
    padding: 0 16px;
    font-weight: inherit;
    vertical-align: middle;

    &:first-child {
      padding-left: 16px;
    }

    &:last-child {
      padding-right: 16px;
    }
  }
}

table.no-actions {
  th:last-child,
  td:last-child {
    width: 50px;
  }
}

.status-header {
  text-align: left;
}

.numeric-header {
  text-align: right;
  width: 90px;

  &:nth-child(3) {
    width: 80px;
  }

  &:nth-child(4) {
    width: 50px;
  }
}

.action-header {
  text-align: right;
  width: 44px;
}

.status-cell {
  .status-dot-wrapper {
    display: inline-block;
    width: 20px;
    height: 20px;
    vertical-align: middle;
    margin-right: 6px;
  }

  > span {
    vertical-align: middle;
  }
}

.numeric-cell {
  text-align: right;
}

.action-cell {
  text-align: right;

  :deep(.icon-button) {
    vertical-align: middle;
  }
}
</style>
